import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';

function TopNavbar({ onLogout, user }) {
  const { hasPermission, canAccessPage } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  const isDropdownActive = () => {
    return ['/asset-types', '/departments', '/discoms', '/vendors'].includes(location.pathname);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const closeDropdown = () => {
    const dropdowns = document.querySelectorAll('.dropdown-toggle');
    dropdowns.forEach(dropdown => {
      const bsDropdown = window.bootstrap?.Dropdown?.getInstance(dropdown);
      if (bsDropdown) {
        bsDropdown.hide();
      }
    });
  };

  const handleDropdownItemClick = () => {
    closeDropdown();
    closeMobileMenu();
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      alert('New password must be at least 6 characters');
      return;
    }

    try {
      await axios.post('/api/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      alert('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      alert(error.response?.data || 'Error changing password');
    }
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark sticky-top">
        <div className="container-fluid">
          <button className="btn btn-link text-white d-lg-none me-2" onClick={() => setMobileMenuOpen(true)}>
            <i className="fas fa-bars fa-lg"></i>
          </button>
          
          <Link className="navbar-brand" to="/" onClick={closeMobileMenu}>
            <i className="fas fa-boxes me-2"></i>Saral Asset
          </Link>
          
          <div className="d-none d-lg-flex flex-grow-1">
            <ul className="navbar-nav me-auto">
              <li className="nav-item">
                <NavLink className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} to="/">
                  <i className="fas fa-tachometer-alt me-1"></i> Dashboard
                </NavLink>
              </li>
              {canAccessPage('canManageAssets') && (
                <li className="nav-item dropdown">
                  <button className={`nav-link dropdown-toggle btn btn-link text-start ${isDropdownActive() ? 'active' : ''}`} data-bs-toggle="dropdown">
                    <i className="fas fa-database me-1"></i> Master Data
                  </button>
                  <ul className="dropdown-menu">
                    <li><Link className={`dropdown-item ${location.pathname === '/asset-types' ? 'active' : ''}`} to="/asset-types">
                      <i className="fas fa-tags me-2"></i>Asset Types
                    </Link></li>
                    <li><Link className={`dropdown-item ${location.pathname === '/departments' ? 'active' : ''}`} to="/departments">
                      <i className="fas fa-building me-2"></i>Departments
                    </Link></li>
                    <li><Link className={`dropdown-item ${location.pathname === '/discoms' ? 'active' : ''}`} to="/discoms">
                      <i className="fas fa-network-wired me-2"></i>Discoms
                    </Link></li>
                    <li><Link className={`dropdown-item ${location.pathname === '/vendors' ? 'active' : ''}`} to="/vendors">
                      <i className="fas fa-truck me-2"></i>Vendors
                    </Link></li>
                  </ul>
                </li>
              )}
              {canAccessPage('canManageAssets') && (
                <li className="nav-item">
                  <NavLink className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} to="/assets">
                    <i className="fas fa-laptop me-1"></i> Assets
                  </NavLink>
                </li>
              )}
              {canAccessPage('canManageEmployees') && (
                <li className="nav-item">
                  <NavLink className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} to="/employees">
                    <i className="fas fa-users me-1"></i> Employees
                  </NavLink>
                </li>
              )}
              {canAccessPage('canManageAssignments') && (
                <li className="nav-item">
                  <NavLink className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} to="/assignments">
                    <i className="fas fa-exchange-alt me-1"></i> Assignments
                  </NavLink>
                </li>
              )}
              {canAccessPage('canManageRepairs') && (
                <li className="nav-item">
                  <NavLink className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} to="/repairs">
                    <i className="fas fa-wrench me-1"></i> Repairs
                  </NavLink>
                </li>
              )}
              {canAccessPage('canManageUsers') && (
                <li className="nav-item">
                  <NavLink className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} to="/users">
                    <i className="fas fa-users-cog me-1"></i> Users
                  </NavLink>
                </li>
              )}
              {canAccessPage('canManageUsers') && (
                <li className="nav-item ai-search-menu">
                  <NavLink className={({ isActive }) => `nav-link ai-search-link ${isActive ? 'active' : ''}`} to="/ai-search">
                    <i className="fas fa-brain me-1 ai-icon"></i> 
                    <span className="ai-text">AssetGPT</span>
                    <span className="ai-badge">BETA</span>
                  </NavLink>
                </li>
              )}
            </ul>
            
            <div className="navbar-nav">
              <NavLink className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} to="/help">
                <i className="fas fa-question-circle me-1"></i> Help
              </NavLink>
              <button 
                className="btn btn-outline-light me-2 theme-toggle" 
                onClick={toggleTheme}
                title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'} me-1`}></i>
                <span className="theme-text">{isDarkMode ? 'Light' : 'Dark'}</span>
              </button>
              <div className="nav-item dropdown">
                <button className="btn btn-outline-light dropdown-toggle" data-bs-toggle="dropdown">
                  <i className="fas fa-user me-2"></i>{user.username}
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li><Link className="dropdown-item" to="/profile/security" onClick={handleDropdownItemClick}>
                    <i className="fas fa-shield-alt me-2"></i>Security Settings
                  </Link></li>
                  <li><hr className="dropdown-divider" /></li>
                  {user?.role === 'Admin' && (
                    <li><Link className="dropdown-item" to="/audit-logs" onClick={handleDropdownItemClick}>
                      <i className="fas fa-clipboard-list me-2"></i>Audit Logs
                    </Link></li>
                  )}
                  {user?.role === 'Admin' && <li><hr className="dropdown-divider" /></li>}
                  <li><button className="dropdown-item" onClick={() => setShowPasswordModal(true)}>
                    <i className="fas fa-key me-2"></i>Change Password
                  </button></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li><button className="dropdown-item" onClick={onLogout}>
                    <i className="fas fa-sign-out-alt me-2"></i>Logout
                  </button></li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="d-lg-none">
            <button className="btn btn-outline-light btn-sm" onClick={toggleTheme}>
              <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && <div className="mobile-menu-backdrop" onClick={closeMobileMenu}></div>}
      
      <div className={`mobile-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-sidebar-header">
          <h5><i className="fas fa-boxes me-2"></i>Menu</h5>
          <button className="btn btn-link text-white" onClick={closeMobileMenu}>
            <i className="fas fa-times fa-lg"></i>
          </button>
        </div>
        <div className="mobile-sidebar-body">
          <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/" onClick={closeMobileMenu}>
            <i className="fas fa-tachometer-alt me-2"></i> Dashboard
          </NavLink>
          <div className="mobile-nav-section">Profile</div>
          <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/profile/security" onClick={closeMobileMenu}>
            <i className="fas fa-shield-alt me-2"></i>Security Settings
          </NavLink>
          <button className="mobile-nav-link" onClick={() => { setShowPasswordModal(true); closeMobileMenu(); }}>
            <i className="fas fa-key me-2"></i>Change Password
          </button>
          {canAccessPage('canManageAssets') && (
            <>
              <div className="mobile-nav-section">Master Data</div>
              <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/asset-types" onClick={closeMobileMenu}>
                <i className="fas fa-tags me-2"></i>Asset Types
              </NavLink>
              <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/departments" onClick={closeMobileMenu}>
                <i className="fas fa-building me-2"></i>Departments
              </NavLink>
              <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/discoms" onClick={closeMobileMenu}>
                <i className="fas fa-network-wired me-2"></i>Discoms
              </NavLink>
              <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/vendors" onClick={closeMobileMenu}>
                <i className="fas fa-truck me-2"></i>Vendors
              </NavLink>
            </>
          )}
          {canAccessPage('canManageAssets') && (
            <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/assets" onClick={closeMobileMenu}>
              <i className="fas fa-laptop me-2"></i> Assets
            </NavLink>
          )}
          {canAccessPage('canManageEmployees') && (
            <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/employees" onClick={closeMobileMenu}>
              <i className="fas fa-users me-2"></i> Employees
            </NavLink>
          )}
          {canAccessPage('canManageAssignments') && (
            <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/assignments" onClick={closeMobileMenu}>
              <i className="fas fa-exchange-alt me-2"></i> Assignments
            </NavLink>
          )}
          {canAccessPage('canManageRepairs') && (
            <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/repairs" onClick={closeMobileMenu}>
              <i className="fas fa-wrench me-2"></i> Repairs
            </NavLink>
          )}
          {canAccessPage('canManageUsers') && (
            <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/users" onClick={closeMobileMenu}>
              <i className="fas fa-users-cog me-2"></i> Users
            </NavLink>
          )}
          {canAccessPage('canManageUsers') && (
            <NavLink className={({ isActive }) => `mobile-nav-link ai-search-link ${isActive ? 'active' : ''}`} to="/ai-search" onClick={closeMobileMenu}>
              <i className="fas fa-brain me-2 ai-icon"></i> 
              <span className="ai-text">SaralAssetGPT</span>
              <span className="ai-badge ms-2">BETA</span>
            </NavLink>
          )}
          <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/help" onClick={closeMobileMenu}>
            <i className="fas fa-question-circle me-2"></i> Help
          </NavLink>
          <div className="mobile-nav-divider"></div>
          {user?.role === 'Admin' && (
            <NavLink className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} to="/audit-logs" onClick={closeMobileMenu}>
              <i className="fas fa-clipboard-list me-2"></i>Audit Logs
            </NavLink>
          )}
          <button className="mobile-nav-link" onClick={() => { onLogout(); closeMobileMenu(); }}>
            <i className="fas fa-sign-out-alt me-2"></i>Logout
          </button>
          <div className="mobile-sidebar-footer">
            <small className="text-muted">Logged in as {user.username}</small>
          </div>
        </div>
      </div>

      {showPasswordModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-key me-2"></i>Change Password
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowPasswordModal(false)}></button>
              </div>
              <form onSubmit={handleChangePassword}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Current Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">New Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Confirm New Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Change Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default TopNavbar;
